/**
 * Documents stored for the logged-in employee. The list comes from the
 * employee API, which returns only rows for that login. Document type and
 * year narrow that list. The PDF can be opened or saved under its original name.
 */

import React, { useEffect, useMemo, useState } from 'react';
import '../SalaryDataUploadDownload/css/DataDisplay.css';
import './css/DocumentDelivery.css';
import { DOCUMENT_TYPES } from './documentTypes';
import {
  EmployeeDocumentItem,
  downloadMyDocument,
  listMyDocuments,
  openMyDocument,
} from '../../services/EmployeeDocumentService';

function periodLabel(document: EmployeeDocumentItem): string {
  if (document.revisionMonth > 0) {
    return `令和${document.eraYear}年${document.revisionMonth}月`;
  }
  return '';
}

export const EmployeeDocuments: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [documents, setDocuments] = useState<EmployeeDocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    listMyDocuments().then((result) => {
      if (!active) return;
      setDocuments(result.data);
      setError(result.success ? '' : result.message);
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const years = useMemo(() => {
    const values = documents.map((document) => document.eraYear);
    return Array.from(new Set(values)).sort((a, b) => b - a);
  }, [documents]);

  const filtered = documents.filter((document) => {
    if (typeFilter && document.documentTypeLabel !== typeFilter) return false;
    if (yearFilter && String(document.eraYear) !== yearFilter) return false;
    return true;
  });

  const clearFilters = () => {
    setTypeFilter('');
    setYearFilter('');
  };

  const openDocument = async (id: number) => {
    setOpeningId(id);
    setError('');
    const result = await openMyDocument(id);
    if (!result.success) setError(result.message);
    setOpeningId(null);
  };

  const downloadDocument = async (document: EmployeeDocumentItem) => {
    setDownloadingId(document.id);
    setError('');
    const result = await downloadMyDocument(document.id, document.originalFileName);
    if (!result.success) setError(result.message);
    setDownloadingId(null);
  };

  return (
    <div className="tab-container">
      {!embedded && (
        <div className="tab-header">
          <h3>書類</h3>
          <p>登録された書類を確認できます</p>
        </div>
      )}
      <div className="tab-content document-list">
        <div className="controls-section">
          <div className="filter-operations">
            <div className="filter-group">
              <label htmlFor="my-document-type-filter">書類の種類:</label>
              <select
                id="my-document-type-filter"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
              >
                <option value="">すべて</option>
                {DOCUMENT_TYPES.map((definition) => (
                  <option key={definition.id} value={definition.label}>{definition.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="my-document-year-filter">年:</label>
              <select
                id="my-document-year-filter"
                value={yearFilter}
                onChange={(event) => setYearFilter(event.target.value)}
              >
                <option value="">すべて</option>
                {years.map((year) => (
                  <option key={year} value={String(year)}>{`令和${year}年`}</option>
                ))}
              </select>
            </div>
            <button type="button" className="clear-filters" onClick={clearFilters}>
              フィルタークリア
            </button>
          </div>
        </div>

        {isLoading && <div className="no-data"><p>読み込み中...</p></div>}
        {!isLoading && error && <div className="delete-error" role="alert"><span>{error}</span></div>}
        {!isLoading && !error && documents.length === 0 && (
          <div className="no-data"><p>書類はまだありません。</p></div>
        )}
        {!isLoading && !error && documents.length > 0 && filtered.length === 0 && (
          <div className="no-data"><p>条件に一致する書類はありません。</p></div>
        )}
        {!isLoading && filtered.length > 0 && (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>書類の種類</th>
                  <th>対象者</th>
                  <th>年</th>
                  <th>改定年月</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((document) => (
                  <tr key={document.id}>
                    <td>{document.documentTypeLabel}</td>
                    <td>{document.personName}</td>
                    <td>{`令和${document.eraYear}年`}</td>
                    <td>{periodLabel(document)}</td>
                    <td>
                      <button
                        type="button"
                        className="preview-row-btn"
                        onClick={() => openDocument(document.id)}
                        disabled={openingId === document.id || downloadingId === document.id}
                      >
                        {openingId === document.id ? '開いています...' : '開く'}
                      </button>
                      <button
                        type="button"
                        className="preview-row-btn"
                        onClick={() => downloadDocument(document)}
                        disabled={openingId === document.id || downloadingId === document.id}
                      >
                        {downloadingId === document.id ? 'ダウンロード中...' : 'ダウンロード'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
